import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateDesignDto } from "../../core/models/dtos/CreateDesignDto";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { getActAsOrgId } from "../../contexts/actAsStore";
import { PageQuery, toQueryParam } from "../../utils/models/pageQuery";
import { Design } from "../../core/models/entities/Design";
import { PagedResponse } from "../models/responses/pagedResponse";
import { mapToDesign } from "../models/mappers/mapDesign";
import { InitiateApproval } from "../../presentation/screens/logo-list/hooks/useLogoActionsHandler";
import { Verdict } from "../../presentation/components/ApprovalVoting/approval-voting";
import { ChangeStyleBody, ChangeTitleBody } from "../models/bodies/ChangeBody";
import { CreateMaskQueueCount } from "../models/responses/CreateMaskQueueCount";

import { DesignDto, DesignNoteDto } from "../models/responses/DesignDto";

export interface DesignRepository {
  useAllDesigns: (
    query: PageQuery,
    options?: { keepPreviousData?: boolean },
  ) => Resource<PagedResponse<Design>>;
  prefetchDesigns: (query: PageQuery) => Promise<void>;
  useDesign: (organizationId: number, designId: number) => Resource<Design>;
  createDesign: (dto: CreateDesignDto) => Promise<Design>;
  updateDesign: (design: Design) => Promise<Design>;
  commit(design: Design): Promise<Design>;
  initiateApproval(design: Design, approval: InitiateApproval): Promise<Design>;
  placeVerdict(design: Design, verdict: Verdict): Promise<Design>;
  deleteDesign(orgId: number, designId: number): Promise<void>;
  skipApproval(design: Design): Promise<Design>;
  sendApproval(design: Design, email: string): Promise<void>;
  sendApprovalConditional(design: Design, email: string): Promise<void>;
  changeStyle(
    orgId: number,
    designId: number,
    styleId: number
  ): Promise<Design>;
  changeTitle(orgId: number, designId: number, title: string): Promise<Design>;
  updateDesignNote(
    orgId: number,
    designId: number,
    note: string | null
  ): Promise<DesignNoteDto>;
  copyDesign(orgId: number, designId: number): Promise<Design>;
  getImportDesignsQueueCounts(): Promise<CreateMaskQueueCount[]>;
  useImportDesignsQueueCounts(): Resource<CreateMaskQueueCount[]>;
  getDesignPrices(designIds: number[]): Promise<Design[]>;
  updatePricesForDesigns(query: PageQuery, designIds: number[]): Promise<void>;
}

export function useDesignRepository(): DesignRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  // Act-as-vendor: getAllDesigns is scoped by ApiClient (actAsOrgId param), so
  // the impersonation scope must be part of the list cache keys — otherwise
  // the admin's global rows are served from cache after switching identity.
  // 0 = not impersonating. See OrderRepository for the full rationale.
  const actAsKey = () => getActAsOrgId() ?? 0;

  async function invalidateQueries() {
    await queryClient.invalidateQueries({
      queryKey: ["designs"],
      type: "all",
    });
  }

  function useAllDesigns(
    query: PageQuery,
    options?: { keepPreviousData?: boolean },
  ): Resource<PagedResponse<Design>> {
    return useQuery(
      ["designs", actAsKey(), toQueryParam(query)],
      ({ signal }) => apiClient.getAllDesigns(query, false, signal),
      {
        // Null-guard on results: a throw inside select puts the query in a
        // 200-backed error state that never heals (same failure mode as the
        // mapMultiProductRef clone bug).
        select: (data) => ({
          ...data,
          results: data.results ? data.results.map(mapToDesign) : [],
        }),
        // OPT-IN (default off): the design list keeps the previous page's
        // rows visible (dimmed via isFetching/isPreviousData in
        // FunctionalList) while a new search/page loads, instead of
        // collapsing into a spinner. Other consumers (admin design import)
        // rely on isLoading gating and keep the previous behavior.
        keepPreviousData: options?.keepPreviousData ?? false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        // Keep the list cached past React Query's 5 min GC default so a
        // Logo->Design hop after a pause still lands on cache. MUST be paired
        // with refetchOnMount: true (overriding the global false): a stale
        // cached page then renders instantly and revalidates in the
        // background, so freshness is never worse than today.
        cacheTime: 30 * 60 * 1000,
        refetchOnMount: true,
      }
    );
  }

  // Warm a page's cache in the background (same key, call, staleTime and
  // cacheTime as useAllDesigns, so a later click is served instantly from
  // cache). Returns the never-rejecting prefetch promise for in-flight tracking.
  function prefetchDesigns(query: PageQuery): Promise<void> {
    return queryClient.prefetchQuery(
      ["designs", actAsKey(), toQueryParam(query)],
      () => apiClient.getAllDesigns(query, false),
      { staleTime: 5 * 60 * 1000, cacheTime: 30 * 60 * 1000 }
    );
  }

  function useDesign(
    organizationId: number,
    designId: number
  ): Resource<Design> {
    // actAsKey in the DETAIL key too: getDesign now carries actAsOrgId, so the
    // payload varies with the impersonation scope (per-org number-logo storage
    // counts). Same slot position as the list keys; useCartSync's design
    // queries share this key shape and must stay in sync.
    return useQuery(
      ["designs", actAsKey(), designId],
      () => apiClient.getDesign(organizationId, designId),
      {
        select: (data) => mapToDesign(data),
      }
    );
  }

  function createDesign(dto: CreateDesignDto): Promise<Design> {
    return apiClient.createDesign(dto).then((designResponse) => {
      invalidateQueries().then();
      return mapToDesign(designResponse);
    });
  }

  function updateDesign(design: Design): Promise<Design> {
    return apiClient
      .updateDesign(design.customerId, design.id, design)
      .then((designResponse) => {
        invalidateQueries().then();
        return mapToDesign(designResponse);
      });
  }

  function commit(design: Design): Promise<Design> {
    return apiClient.commitDesign(design).then((designResponse) => {
      invalidateQueries().then();
      return mapToDesign(designResponse);
    });
  }

  function initiateApproval(
    design: Design,
    approval: InitiateApproval
  ): Promise<Design> {
    return apiClient
      .initiateApprovalDesign(design, approval)
      .then((designResponse) => {
        invalidateQueries().then();
        return mapToDesign(designResponse);
      });
  }

  function placeVerdict(design: Design, verdict: Verdict): Promise<Design> {
    return apiClient
      .placeVerdictDesign(design, verdict)
      .then((designResponse) => {
        invalidateQueries().then();
        return mapToDesign(designResponse);
      });
  }

  function deleteDesign(orgId: number, designId: number): Promise<void> {
    return apiClient.deleteDesign(orgId, designId).then(async () => {
      await invalidateQueries();
    });
  }

  function skipApproval(design: Design): Promise<Design> {
    return apiClient.skipApprovalDesign(design).then((designResponse) => {
      invalidateQueries().then();
      return mapToDesign(designResponse);
    });
  }

  async function sendApproval(design: Design, email: string): Promise<void> {
    const lr = await apiClient.sendApprovalDesign(design, email);
  }

  async function sendApprovalConditional(
    design: Design,
    email: string
  ): Promise<void> {
    const lr = await apiClient.sendApprovalDesignConditional(design, email);
  }

  async function changeStyle(
    orgId: number,
    designId: number,
    styleId: number
  ): Promise<Design> {
    const body: ChangeStyleBody = {
      styleId: styleId,
    };

    const dto = await apiClient.changeStyle(orgId, designId, body);
    // The PUT response IS the updated design (same DesignDto the GET endpoint
    // returns). Seed the detail cache with the raw DTO — useDesign stores raw
    // DTOs and maps via `select` — so the editor mounts without its own GET.
    queryClient.setQueryData(["designs", actAsKey(), designId], dto);
    // Non-blocking list invalidation (same pattern as createDesign /
    // updateDesign): the design list refreshes in the background instead of
    // holding the style-change flow hostage.
    invalidateQueries().then();
    return mapToDesign(dto);
  }

  async function changeTitle(
    orgId: number,
    designId: number,
    title: string
  ): Promise<Design> {
    const body: ChangeTitleBody = {
      newName: title,
    };

    const result = apiClient
      .changeDesignTitle(orgId, designId, body)
      .then(async (res) => {
        await invalidateQueries();
        return res;
      });
    return result;
  }

  // Customer-editable design note. Optimistic-ish cache update: the PUT response
  // carries the saved note + audit stamp, so on success we patch the DETAIL cache
  // in place (raw DTO — useDesign maps via `select`) instead of refetching the
  // whole design, and refresh the list caches in the background (non-blocking,
  // same pattern as changeStyle) so list payloads pick the note up eventually.
  async function updateDesignNote(
    orgId: number,
    designId: number,
    note: string | null
  ): Promise<DesignNoteDto> {
    const res = await apiClient.updateDesignNote(orgId, designId, note);
    queryClient.setQueryData(
      ["designs", actAsKey(), designId],
      (old: DesignDto | undefined) =>
        old
          ? {
              ...old,
              customerNote: res.customerNote,
              customerNoteUpdatedAtUtc: res.customerNoteUpdatedAtUtc,
              customerNoteUpdatedByName: res.customerNoteUpdatedByName,
            }
          : old
    );
    invalidateQueries().then();
    return res;
  }

  async function copyDesign(orgId: number, designId: number): Promise<Design> {
    const dto = await apiClient.copyDesign(orgId, designId);
    // The copy response IS the new design (same DesignDto shape as GET). Seed
    // the detail cache under the NEW id so the editor mounts without its own
    // GET, and refresh the design list in the background instead of blocking
    // navigation on a refetch of the whole list.
    queryClient.setQueryData(["designs", actAsKey(), dto.id], dto);
    // The copy response carries no prices (the backend only runs AdjustPrice on
    // GET), and the global refetchOnMount:false means a seeded-but-inactive key
    // would otherwise never heal — the details modal would show "no available
    // prices" from the stale seed. Force a background refetch of the seeded key
    // (refetchType "all" covers inactive queries) while the editor mounts
    // instantly from the seed.
    queryClient
      .invalidateQueries({
        queryKey: ["designs", actAsKey(), dto.id],
        refetchType: "all",
      })
      .then();
    invalidateQueries().then();
    return mapToDesign(dto);
  }

  function getImportDesignsQueueCounts(): Promise<CreateMaskQueueCount[]> {
    return apiClient.getImportDesignsQueueCounts();
  }

  // React Query version for the header queue badge: dedups the header's repeated
  // remounts into one request (the imperative call used to fire ~5x per page load)
  // while keeping the 60s polling via refetchInterval.
  function useImportDesignsQueueCounts(): Resource<CreateMaskQueueCount[]> {
    return useQuery(
      ["import-queue-counts", "designs"],
      () => apiClient.getImportDesignsQueueCounts(),
      {
        refetchInterval: 60000,
        staleTime: 55000,
      }
    );
  }

  function getDesignPrices(designIds: number[]): Promise<Design[]> {
    return apiClient
      .getDesignPrices(designIds)
      .then((dtos: DesignDto[]) => (dtos ?? []).map(mapToDesign));
  }

  async function updatePricesForDesigns(
    query: PageQuery,
    designIds: number[]
  ): Promise<void> {
    if (designIds.length === 0) return;
    const pricedDtos = (await apiClient.getDesignPrices(designIds)) ?? [];

    // Same key shape as useAllDesigns/prefetchDesigns — this write targeted
    // ["designs", qs] and has been a no-op since actAsKey() entered the list
    // key (#601). Currently uncalled (the list's price column was removed),
    // but kept correct for a future resurrection.
    queryClient.setQueryData(
      ["designs", actAsKey(), toQueryParam(query)],
      (oldData: PagedResponse<DesignDto> | undefined) => {
        if (!oldData?.results) return oldData;

        const newResults = oldData.results.map((dto) => {
          const priced = pricedDtos.find((p) => p.id === dto.id);
          if (priced) {
            return {
              ...dto,
              product: priced.product,
              heating: priced.heating,
              priceSort: priced.priceSort,
              designLogos: priced.designLogos,
            };
          }
          return dto;
        });

        return {
          ...oldData,
          results: newResults,
        };
      }
    );
  }

  /*
  *
  delete(orgId: number, designId: number): Promise<void>
  * */

  return {
    useAllDesigns,
    prefetchDesigns,
    useDesign,
    createDesign,
    updateDesign,
    commit,
    deleteDesign,
    placeVerdict,
    initiateApproval,
    skipApproval,
    sendApproval,
    sendApprovalConditional,
    changeStyle,
    changeTitle,
    updateDesignNote,
    copyDesign,
    getImportDesignsQueueCounts,
    useImportDesignsQueueCounts,
    getDesignPrices,
    updatePricesForDesigns,
  };
}
